"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import PixelGridLoader from "@/components/common/PixelGridLoader";
import Icon from "@/components/library/Icon";
import type {collectOmegaProgress, OmegaProgressStep} from "@/services/wingman/omega-results";

export type WorkflowProgress = ReturnType<typeof collectOmegaProgress>;

export function useWorkflowProgressLabel(progress: WorkflowProgress) {
   const {t} = useTranslation("ask");
   const current = progress.pending.at(-1);
   return current ? t(`workflowPanel.activity.${current.label}`) : t(`workflowPanel.progress.${progress.next}`);
}

/** Live feedback is based on tool events, never on a timer or an invented percentage. */
export default function WorkflowBuildProgress({progress}: {progress: WorkflowProgress}) {
   const {t} = useTranslation("ask");
   const label = useWorkflowProgressLabel(progress);
   const recent = progress.steps.filter(step => step.state !== "pending").reduce<(OmegaProgressStep & {count: number})[]>((groups, step) => {
      const previous = groups.at(-1);
      if (previous?.label === step.label && previous.state === step.state) previous.count++;
      else groups.push({...step, count: 1});
      return groups;
   }, []).slice(-3);

   return (
      <div className="overflow-hidden rounded-xl border border-primary/20 bg-panel dark:border-blue-400/25">
         <div className="bg-primary/5 p-4 dark:bg-blue-400/5">
            <div className="flex items-center justify-between gap-3">
               <span className="text-xs font-medium text-primary dark:text-blue-300">{t("workflowPanel.progress.title")}</span>
               <span className="flex shrink-0 items-center gap-1.5 text-xs text-primary dark:text-blue-300">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current motion-safe:animate-pulse" />
                  {t("workflowPanel.progress.live")}
               </span>
            </div>
            <output aria-atomic="true" className="mt-3 flex items-start gap-3">
               <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-panel text-primary dark:border-blue-400/20 dark:text-blue-300">
                  <PixelGridLoader />
               </span>
               <span className="min-w-0">
                  <span className="block w-fit animate-text-shimmer text-sm font-medium leading-5 text-600 motion-reduce:animate-none motion-reduce:[-webkit-text-fill-color:currentColor]">{label}</span>
                  <span className="mt-1 block text-xs leading-5 text-600">{t("workflowPanel.progress.hint")}</span>
               </span>
            </output>
         </div>
         {recent.length > 0 && <div className="border-t border-primary/10 px-4 py-3 dark:border-blue-400/15">
            <p className="mb-2 text-xs text-500">{t("workflowPanel.progress.returned", {count: progress.returned})}</p>
            <ul className="space-y-2">
               {recent.map(step => <li key={step.id} className="flex items-center gap-2 text-xs text-600">
                  <Icon icon={step.state === "error" ? "rotate-right" : "check"} className={clsx("h-3 w-3 shrink-0", step.state === "error" ? "text-amber-600 dark:text-amber-300" : "text-primary dark:text-blue-300")} />
                  <span className="min-w-0 flex-1">{t(`workflowPanel.activity.${step.label}`)}{step.count > 1 && <span className="ml-1.5 text-500">×{step.count}</span>}</span>
                  {step.state === "error" && <span className="text-500">{t("workflowPanel.progress.feedback")}</span>}
               </li>)}
            </ul>
         </div>}
      </div>
   );
}
